// client/src/pages/Game/Battlefield/components/DevPanel.jsx

import React from 'react';

// Éléments circulaires: un seul paramètre de taille (le rayon/diamètre)
const CIRCULAR_ELEMENTS = new Set(['playerLife', 'opponentLife']);

// Éléments circulaires qui ont aussi des cristaux d'énergie et des perles de mana
const ELEMENTS_WITH_ORBS = new Set(['playerLife', 'opponentLife']);

// Contrôles de police individuelle (clé = variable CSS --fs-<key>)
const FONT_CONTROLS = [
  { key: 'life', label: 'PV' },
  { key: 'monster', label: 'Stats monstres' },
  { key: 'cost', label: 'Coûts de cartes (texte)' },
  { key: 'gem', label: 'Gemmes de coût (cristaux sur cartes)' },
  { key: 'deck', label: 'Compteurs deck' },
  { key: 'action', label: 'Bouton action' },
  { key: 'zone', label: 'Zones de combat' },
];

const sliderRowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

const labelStyle = {
  fontSize: '11px',
  color: '#9fd8ff',
  display: 'flex',
  justifyContent: 'space-between',
  marginBottom: '2px',
};

const resetBtnStyle = {
  fontSize: '10px',
  padding: '1px 6px',
  background: 'rgba(0,0,0,0.3)',
  border: '1px solid rgba(90,205,255,0.5)',
  borderRadius: '3px',
  color: 'white',
  cursor: 'pointer',
  flexShrink: 0,
};

const DevPanel = ({
  elementPositions,
  elementSizes,
  updateElementPosition,
  updateElementSize,
  adminSecret,
  updateAdminSecret,
  onSave,
  onReset,
  onExportLayout,
  isCollapsed,
  onToggleCollapse,
  position,
  onDragStart,
  panelRef,
  fontScale = 1.0,
  onUpdateFontScale,
  fontScales = {},
  onUpdateElementFontScale,
  onResetFonts
}) => {
  return (
    <div
      ref={panelRef}
      className={`dev-info ${isCollapsed ? 'collapsed' : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: isCollapsed ? 'auto' : '300px'
      }}
    >
      <div
        className="dev-info-header"
        onMouseDown={onDragStart}
        onTouchStart={onDragStart}
      >
        <h3>Mode Développeur</h3>
        <button className="dev-info-toggle" onClick={onToggleCollapse}>
          {isCollapsed ? '▼' : '▲'}
        </button>
      </div>

      <div className="dev-info-content">
        <div className="dev-secret-container">
          <input
            type="text"
            className="dev-secret-input"
            value={adminSecret}
            onChange={(e) => updateAdminSecret(e.target.value)}
            placeholder="Code secret administrateur"
          />
        </div>

        {/* ── Réglages de police ── */}
        <div style={{
          marginBottom: '10px',
          padding: '6px 0',
          borderBottom: '1px solid rgba(90, 205, 255, 0.3)',
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '6px',
          }}>
            <span style={{ fontSize: '12px', color: '#5ACDFF', fontWeight: 'bold' }}>
              Polices
            </span>
            {onResetFonts && (
              <button style={resetBtnStyle} onClick={onResetFonts}>Réinit. tout</button>
            )}
          </div>

          {/* ◆ Cristaux / ● Perles autour des cercles de PV — réglage direct
              (les deux joueurs), plus finement ajustable par élément dans la
              section Positions ci-dessous. */}
          {[
            { key: 'crystalSize', label: '◆ Cristaux énergie (PV)', def: 1.2, min: 0.4, max: 5, step: 0.05 },
            { key: 'crystalRingGap', label: '◆ Rayon cristaux (espace)', def: 0.3, min: 0, max: 4, step: 0.05 },
            { key: 'pearlSize', label: '● Gemmes de mana (PV)', def: 1, min: 0.4, max: 4, step: 0.05 },
            { key: 'pearlGap', label: '● Gemmes — dist. au cercle', def: 0, min: 0, max: 20, step: 0.1 },
            { key: 'pearlSpacing', label: '● Gemmes — espacement', def: 0.4, min: 0, max: 3, step: 0.05 },
          ].map(({ key, label, def, min, max, step }) => {
            const value = parseFloat(elementSizes?.playerLife?.[key] ?? def);
            return (
              <div key={key} style={{ marginBottom: '6px' }}>
                <label style={labelStyle}>
                  <span>{label}</span>
                  <span>{value.toFixed(2)}cqw</span>
                </label>
                <div style={sliderRowStyle}>
                  <input
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={value}
                    onChange={(e) => {
                      updateElementSize('playerLife', key, e.target.value);
                      updateElementSize('opponentLife', key, e.target.value);
                    }}
                    style={{ flex: 1, accentColor: '#5ACDFF' }}
                  />
                  <button style={resetBtnStyle} onClick={() => {
                    updateElementSize('playerLife', key, def);
                    updateElementSize('opponentLife', key, def);
                  }}>↺</button>
                </div>
              </div>
            );
          })}

          {/* Multiplicateur maître */}
          <div style={{ marginBottom: '8px' }}>
            <label style={{ ...labelStyle, color: '#5ACDFF', fontWeight: 'bold' }}>
              <span>Toutes (maître)</span>
              <span>{fontScale.toFixed(2)}x</span>
            </label>
            <div style={sliderRowStyle}>
              <input
                type="range"
                min="0.5"
                max="3"
                step="0.05"
                value={fontScale}
                onChange={(e) => onUpdateFontScale && onUpdateFontScale(e.target.value)}
                style={{ flex: 1, accentColor: '#5ACDFF' }}
              />
              <button style={resetBtnStyle} onClick={() => onUpdateFontScale && onUpdateFontScale(1)}>1x</button>
            </div>
          </div>

          {/* Échelles individuelles */}
          {FONT_CONTROLS.map(({ key, label }) => {
            const value = fontScales[key] ?? 1.0;
            return (
              <div key={key} style={{ marginBottom: '6px' }}>
                <label style={labelStyle}>
                  <span>{label}</span>
                  <span>{value.toFixed(2)}x</span>
                </label>
                <div style={sliderRowStyle}>
                  <input
                    type="range"
                    min="0.5"
                    max="3"
                    step="0.05"
                    value={value}
                    onChange={(e) => onUpdateElementFontScale && onUpdateElementFontScale(key, e.target.value)}
                    style={{ flex: 1, accentColor: '#5ACDFF' }}
                  />
                  <button
                    style={resetBtnStyle}
                    onClick={() => onUpdateElementFontScale && onUpdateElementFontScale(key, 1)}
                  >1x</button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="element-positions">
          {Object.entries(elementPositions).map(([key, pos]) => {
            const isCircle = CIRCULAR_ELEMENTS.has(key);
            return (
              <div key={key} className="element-editor">
                <h4>{key} {isCircle && '⬤'}</h4>
                <div className="position-controls">
                  <div className="position-entry">
                    <label>X%</label>
                    <input
                      type="number"
                      className="position-input"
                      value={Math.round(pos.x)}
                      onChange={(e) => updateElementPosition(key, 'x', e.target.value)}
                    />
                  </div>
                  <div className="position-entry">
                    <label>Y%</label>
                    <input
                      type="number"
                      className="position-input"
                      value={Math.round(pos.y)}
                      onChange={(e) => updateElementPosition(key, 'y', e.target.value)}
                    />
                  </div>
                </div>
                {isCircle ? (
                  /* Cercle: un seul champ — le diamètre en cqw */
                  <div className="size-controls">
                    <div className="size-entry">
                      <label>⌀ cqw</label>
                      <input
                        type="number"
                        className="size-input"
                        step="0.1"
                        value={elementSizes[key]?.width || 0}
                        onChange={(e) => {
                          updateElementSize(key, 'width', e.target.value);
                          updateElementSize(key, 'height', e.target.value);
                        }}
                      />
                    </div>
                  </div>
                ) : (
                  /* Rectangle: W et H séparés en cqw */
                  <div className="size-controls">
                    <div className="size-entry">
                      <label>W cqw</label>
                      <input
                        type="number"
                        className="size-input"
                        value={elementSizes[key]?.width || 0}
                        onChange={(e) => updateElementSize(key, 'width', e.target.value)}
                      />
                    </div>
                    <div className="size-entry">
                      <label>H cqw</label>
                      <input
                        type="number"
                        className="size-input"
                        value={elementSizes[key]?.height || 0}
                        onChange={(e) => updateElementSize(key, 'height', e.target.value)}
                      />
                    </div>
                  </div>
                )}

                {ELEMENTS_WITH_ORBS.has(key) && (
                  /* Cristaux ◆ / gemmes ● associés au cercle (tailles + rayons) */
                  <div className="size-controls" style={{ marginTop: '4px' }}>
                    <div className="size-entry">
                      <label>◆ Taille</label>
                      <input
                        type="number"
                        className="size-input"
                        step="0.1"
                        value={elementSizes[key]?.crystalSize ?? 1.2}
                        onChange={(e) => updateElementSize(key, 'crystalSize', e.target.value)}
                      />
                    </div>
                    <div className="size-entry">
                      <label>◆ Rayon</label>
                      <input
                        type="number"
                        className="size-input"
                        step="0.1"
                        value={elementSizes[key]?.crystalRingGap ?? 0.3}
                        onChange={(e) => updateElementSize(key, 'crystalRingGap', e.target.value)}
                      />
                    </div>
                    <div className="size-entry">
                      <label>● Taille</label>
                      <input
                        type="number"
                        className="size-input"
                        step="0.1"
                        value={elementSizes[key]?.pearlSize ?? 1}
                        onChange={(e) => updateElementSize(key, 'pearlSize', e.target.value)}
                      />
                    </div>
                    <div className="size-entry">
                      <label>● Dist.</label>
                      <input
                        type="number"
                        className="size-input"
                        step="0.1"
                        value={elementSizes[key]?.pearlGap ?? 0}
                        onChange={(e) => updateElementSize(key, 'pearlGap', e.target.value)}
                      />
                    </div>
                    <div className="size-entry">
                      <label>● Espac.</label>
                      <input
                        type="number"
                        className="size-input"
                        step="0.1"
                        value={elementSizes[key]?.pearlSpacing ?? 0.4}
                        onChange={(e) => updateElementSize(key, 'pearlSpacing', e.target.value)}
                      />
                    </div>
                  </div>
                )}
              </div>
            );
          })})
        </div>

        <div className="dev-controls">
          <button className="dev-btn" onClick={onSave}>Sauvegarder</button>
          <button className="dev-btn" onClick={onExportLayout} title="Télécharge (et copie) le layout actuel en JSON — collez les valeurs dans les defaults de useGameElements.js pour les rendre permanents en prod">Exporter (prod)</button>
          <button className="dev-btn reset" onClick={onReset}>Réinitialiser</button>
        </div>
      </div>
    </div>
  );
};

export default DevPanel;
